import { type DiagnosticAnswers, painsForRoles } from '@shared/prospect-diagnostic';

/** One question per screen. The pains screen shows only when a role in the team has some. */
export type DiagnosticStepKey =
  | 'website' | 'role' | 'team' | 'motions' | 'pains' | 'ai' | 'goal';

export interface DiagnosticDraft extends Omit<DiagnosticAnswers, 'role' | 'teamSize' | 'aiUse'> {
  role: DiagnosticAnswers['role'] | null;
  teamSize: DiagnosticAnswers['teamSize'] | null;
  aiUse: DiagnosticAnswers['aiUse'] | null;
}

/** A change computed from the current draft, so quick successive clicks never overwrite each other. */
export type DraftPatch = Partial<DiagnosticDraft> | ((current: DiagnosticDraft) => Partial<DiagnosticDraft>);

export const emptyDraft =(): DiagnosticDraft => ({
  website: '', role: null, teamRoles: [], teamSize: null,
  motions: [], pains: [], painsOther: '', painsOtherByRole: {}, aiUse: null, aiIdeas: '', goal: '', problem: '',
});

export const diagnosticSteps = (draft: DiagnosticDraft): DiagnosticStepKey[] => [
  // Objectives before the detail per role (Benoit, 03/11): the pains then say where it blocks.
  'website', 'role', 'team', 'motions', 'goal',
  ...(painsForRoles(draft.teamRoles).length > 0 ? ['pains' as const] : []),
  'ai',
];

export const isStepComplete = (step: DiagnosticStepKey, draft: DiagnosticDraft, websiteValid: boolean): boolean => {
  switch (step) {
    case 'website': return websiteValid;
    case 'role': return draft.role !== null;
    case 'team': return draft.teamRoles.length > 0 && draft.teamSize !== null;
    case 'motions': return draft.motions.length > 0;
    // Optional (Benoit, 03/11): the objectives and the problem already say what matters.
    case 'pains': return true;
    case 'ai': return draft.aiUse !== null;
    case 'goal': return true;
  }
};

/** The answers to send, once every required step is complete; null otherwise. */
export const toAnswers = (draft: DiagnosticDraft): DiagnosticAnswers | null => {
  const { role, teamSize, aiUse } = draft;
  if (!role || !teamSize || !aiUse) return null;
  // Pains of roles removed from the team after being picked are dropped.
  const offered = new Set(painsForRoles(draft.teamRoles).map((pain) => pain.key));
  return {
    ...draft, role, teamSize, aiUse,
    pains: draft.pains.filter((key) => offered.has(key)),
    painsOtherByRole: Object.fromEntries(draft.teamRoles.flatMap((key) => {
      const text = (draft.painsOtherByRole?.[key] ?? '').trim();
      return text ? [[key, text]] : [];
    })),
  };
};

export const toggle = <T,>(list: readonly T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
